"use client";
import Link from "next/link";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import Star from "lucide-react/dist/esm/icons/star";
import BadgeCheck from "lucide-react/dist/esm/icons/badge-check";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ListSkeleton } from "@/components/asset-detail/ListSkeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { UserBadges } from "@/components/ui/user-badges";
import dynamic from "next/dynamic";
import { useSession } from "@/contexts/SessionContext";
import { useProfile } from "@/contexts/ProfileContext";
import { useAuthModal } from "@/contexts/AuthModalContext";

// Admin moderation mutations load only behind the isAdmin gate below, so none
// of this ships in the public asset-page bundle.
const ReviewAdminActionsInner = dynamic(
  () =>
    import("@/components/context-actions/ReviewAdminActionsInner").then(
      (m) => m.ReviewAdminActionsInner,
    ),
  { ssr: false },
);
import { OPEN_REVIEW_HINT_KEY } from "@/components/auth/PendingIntentResumer";
import MessageSquare from "lucide-react/dist/esm/icons/message-square";
import { useAssetReviews, useCreateReview, useRespondToReview, type Review } from "@/hooks/useAssetReviews";
import { useUserBadgesBatch } from "@/hooks/useUserBadges";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { trackReviewSubmitted } from "@/lib/analytics";
import { logEvent } from "@/lib/events";
import { humanizeError } from "@/lib/error-humanizer";

interface ReviewsSectionProps {
  assetId: string;
  /** Slug for the post-signup review resume (routes back to this asset). */
  assetSlug?: string;
  avgRating: number | null;
  reviewCount: number;
  /** Viewer is the seller of this asset — unlocks the public reply control. */
  isAssetOwner?: boolean;
  /** Viewer holds this asset (a purchase or free claim that wasn't refunded).
   *  Only holders may review — the reviews INSERT policy enforces the same. */
  canReview?: boolean;
}

function StarRating({
  rating,
  onRatingChange,
  readonly = false,
}: {
  rating: number;
  onRatingChange?: (rating: number) => void;
  readonly?: boolean;
}) {
  const [hoverRating, setHoverRating] = useState(0);

  // Readonly: a single image conveying the value; individual stars are decorative.
  if (readonly) {
    return (
      <div className="flex gap-1" role="img" aria-label={`Rated ${rating} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            aria-hidden="true"
            className={cn(
              "h-5 w-5",
              rating >= star ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"
            )}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex gap-1" role="radiogroup" aria-label="Your rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          role="radio"
          aria-checked={rating === star}
          aria-label={`${star} star${star === 1 ? "" : "s"}`}
          onClick={() => onRatingChange?.(star)}
          onMouseEnter={() => setHoverRating(star)}
          onMouseLeave={() => setHoverRating(0)}
          className="cursor-pointer rounded transition-colors"
        >
          <Star
            aria-hidden="true"
            className={cn(
              "h-5 w-5",
              (hoverRating || rating) >= star
                ? "fill-yellow-400 text-yellow-400"
                : "text-muted-foreground"
            )}
          />
        </button>
      ))}
    </div>
  );
}

/**
 * Admin-only inline moderation for a single review. Split out (and lazily
 * loaded) so the moderation mutations don't ship in the public asset bundle.
 */
function ReviewAdminActions({ reviewId }: { reviewId: string }) {
  const { isAdmin } = useProfile();
  if (!isAdmin) return null;
  return <ReviewAdminActionsInner reviewId={reviewId} />;
}

function ReviewItem({
  review,
  roles,
  assetId,
  canRespond,
}: {
  review: Review;
  roles?: string[];
  assetId: string;
  /** Viewer owns the asset, so they may post a public reply. */
  canRespond: boolean;
}) {
  // Guard: PostgREST may return user as null (deleted profile) or as an array
  // when the FK join is ambiguous.  Normalise to a plain object or null.
  const user =
    review.user && !Array.isArray(review.user) ? review.user : null;
  const displayName =
    user?.display_name || user?.username || "Anonymous";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const userLink = user?.username ? `/store/${user.username}` : null;
  const avatarUrl = user?.avatar_url || undefined;

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          {userLink ? (
            <Link href={userLink}>
              <Avatar className="h-10 w-10 hover:ring-2 hover:ring-primary transition-all">
                <AvatarImage src={avatarUrl} alt={displayName} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </Link>
          ) : (
            <Avatar className="h-10 w-10">
              <AvatarImage src={avatarUrl} alt={displayName} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
          )}
          <div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {userLink ? (
                <Link href={userLink} className="font-medium hover:text-primary transition-colors">
                  {displayName}
                </Link>
              ) : (
                <p className="font-medium">{displayName}</p>
              )}
              <UserBadges
                user={{
                  is_seller: user?.is_seller,
                  seller_verified: user?.seller_verified,
                  is_early_founder: user?.is_early_founder,
                  roles,
                }}
                size="sm"
              />
            </div>
            <p className="text-sm text-muted-foreground">
              {format(new Date(review.created_at), "MMM d, yyyy")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StarRating rating={review.rating} readonly />
          {review.is_verified_purchase && (
            <Badge variant="secondary" className="bg-success/15 text-success dark:text-success border-0 text-2xs font-semibold uppercase tracking-wider gap-1 px-1.5">
              <BadgeCheck className="h-3 w-3" />
              Verified
            </Badge>
          )}
          {/* Moderating a bad review used to mean leaving the page for the
              admin hub and finding the row again. Renders nothing for
              non-admins. */}
          <ReviewAdminActions reviewId={review.id} />
        </div>
      </div>

      {review.title && <h4 className="font-semibold">{review.title}</h4>}

      {review.content && (
        <p className="text-muted-foreground">{review.content}</p>
      )}

      <SellerResponse review={review} assetId={assetId} canRespond={canRespond} />
    </div>
  );
}

/**
 * The asset owner's public reply, and the form to write it.
 *
 * Reviews were read-only by construction before this — a seller could read a
 * review misdescribing their asset and had no way to answer it in public, so
 * the only recourse was asking an admin to delete it.
 */
function SellerResponse({
  review,
  assetId,
  canRespond,
}: {
  review: Review;
  assetId: string;
  canRespond: boolean;
}) {
  const respond = useRespondToReview();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(review.seller_response ?? "");

  const submit = async () => {
    try {
      await respond.mutateAsync({ reviewId: review.id, assetId, response: draft });
      setEditing(false);
      toast.success(draft.trim() ? "Response posted" : "Response removed");
    } catch (error) {
      toast.error(humanizeError(error, "posting your response"));
    }
  };

  if (review.seller_response && !editing) {
    return (
      <div className="ml-4 mt-2 border-l-2 border-primary/30 pl-4">
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-2xs font-semibold uppercase tracking-wider">
            Seller response
          </Badge>
          {review.seller_responded_at && (
            <span className="text-xs text-muted-foreground">
              {format(new Date(review.seller_responded_at), "MMM d, yyyy")}
            </span>
          )}
          {canRespond && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground"
              onClick={() => {
                setDraft(review.seller_response ?? "");
                setEditing(true);
              }}
            >
              Edit
            </Button>
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{review.seller_response}</p>
      </div>
    );
  }

  if (!canRespond) return null;

  if (!editing) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        onClick={() => setEditing(true)}
      >
        <MessageSquare className="mr-1 h-4 w-4" />
        Respond
      </Button>
    );
  }

  return (
    <div className="ml-4 mt-2 space-y-2 border-l-2 border-primary/30 pl-4">
      <Textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="Reply publicly — buyers reading this review will see your answer."
        rows={3}
        maxLength={2000}
      />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={submit} disabled={respond.isPending}>
          {review.seller_response ? "Update response" : "Post response"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setDraft(review.seller_response ?? "");
            setEditing(false);
          }}
        >
          Cancel
        </Button>
        {review.seller_response && (
          <Button
            size="sm"
            variant="ghost"
            className="text-destructive"
            disabled={respond.isPending}
            onClick={async () => {
              setDraft("");
              try {
                await respond.mutateAsync({ reviewId: review.id, assetId, response: "" });
                setEditing(false);
                toast.success("Response removed");
              } catch (error) {
                toast.error(humanizeError(error, "removing your response"));
              }
            }}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  );
}

export function ReviewsSection({
  assetId,
  assetSlug,
  avgRating,
  reviewCount,
  isAssetOwner = false,
  canReview = false,
}: ReviewsSectionProps) {
  const { user } = useSession();
  const { openAuthModal } = useAuthModal();
  const { data: reviews, isLoading } = useAssetReviews(assetId);
  const createReview = useCreateReview();

  // Batch fetch roles for all reviewers
  const reviewerUserIds = (reviews || []).map((r) => r.user?.user_id).filter(Boolean) as string[];
  const { data: rolesMap } = useUserBadgesBatch(reviewerUserIds);

  const [showForm, setShowForm] = useState(false);
  // One review per person per asset (unique index); the list includes the
  // viewer's own review even while it waits for moderation.
  const hasReviewed = !!user && (reviews ?? []).some((r) => r.user?.user_id === user.id);
  const mayWrite = canReview && !isAssetOwner && !hasReviewed;
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  // Auto-open the form when the user just signed up from "Write a Review" —
  // the PendingIntentResumer routes them back here with a sessionStorage hint.
  useEffect(() => {
    if (!user) return;
    try {
      if (sessionStorage.getItem(OPEN_REVIEW_HINT_KEY) === assetId) {
        sessionStorage.removeItem(OPEN_REVIEW_HINT_KEY);
        setShowForm(true);
      }
    } catch {
      // sessionStorage unavailable — the user can still click the button
    }
  }, [user, assetId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      await createReview.mutateAsync({
        assetId,
        rating,
        title: title || undefined,
        content: content || undefined,
      });
      setShowForm(false);
      setRating(5);
      setTitle("");
      setContent("");
      trackReviewSubmitted(assetId, rating);
      logEvent("review_submitted", { asset_id: assetId, rating }, { severity: "medium" });
      toast.success("Review submitted successfully!");
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error(humanizeError(error, "submitting your review"));
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle>Reviews</CardTitle>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {avgRating !== null && (
                <>
                  <StarRating rating={Math.round(avgRating)} readonly />
                  <span className="font-medium">{avgRating.toFixed(1)}</span>
                </>
              )}
              <span className="text-muted-foreground">
                ({reviewCount} {reviewCount === 1 ? "review" : "reviews"})
              </span>
            </div>
            <p className="text-2xs text-muted-foreground mt-1.5 max-w-prose">
              Reviews are written by signed-in accounts. A <span className="font-medium">Verified</span> tag marks
              reviewers we matched to a purchase or download record for this asset. We don&apos;t edit, incentivize,
              or reorder reviews by rating.
            </p>
          </div>
          {!showForm && user && !isAssetOwner && !hasReviewed && !canReview && (
            <p className="text-xs text-muted-foreground sm:max-w-[14rem] sm:text-right">
              Reviews come from people who own this asset.
            </p>
          )}
          {!showForm && (!user || mayWrite) && (
            <Button
              onClick={() => {
                logEvent("review_intent", { asset_id: assetId }, { severity: "medium" });
                if (!user) {
                  // Logged-out reviewers used to see nothing at all — prompt
                  // signup and auto-reopen the form once they're verified.
                  openAuthModal({
                    message: "Write a review",
                    intent: { kind: "review", assetId, assetSlug },
                  });
                  return;
                }
                setShowForm(true);
              }}
              className="w-full sm:w-auto"
            >Write a Review</Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Review Form */}
        {showForm && mayWrite && (
          <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-muted rounded-lg">
            <div>
              <label className="text-sm font-medium mb-2 block">Your Rating</label>
              <StarRating rating={rating} onRatingChange={setRating} />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Title (optional)</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Summarize your experience"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Review (optional)</label>
              <Textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Share your thoughts about this asset…"
                rows={4}
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={createReview.isPending}>
                {createReview.isPending ? "Submitting…" : "Submit Review"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        )}

        {/* Reviews List */}
        {isLoading ? (
          <ListSkeleton rows={2} />
        ) : reviews && reviews.length > 0 ? (
          <div className="space-y-6">
            {reviews.map((review, index) => (
              <div key={review.id}>
                <ReviewItem
                  review={review}
                  roles={rolesMap?.[review.user?.user_id || ""] || []}
                  assetId={assetId}
                  canRespond={isAssetOwner}
                />
                {index < reviews.length - 1 && <Separator className="mt-6" />}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-8">
            No reviews yet. Be the first to review this asset!
          </p>
        )}
      </CardContent>
    </Card>
  );
}
